import { useCallback, useEffect } from 'react';

const OVERLAY_SELECTOR = '.overlay';

/**
 * ncoded's Overlay locks the page behind a modal with `body-scroll-lock`, calling
 * it as `disableBodyScroll(document.body)`. On touch devices that library blocks
 * every single-finger `touchmove` on `document` and only lets through the ones
 * that pass its bounds check against the element it was handed — here
 * `document.body`, not the modal. So with the page at the top
 * (`body.scrollTop === 0`) it `preventDefault`s every downward swipe, and a modal
 * taller than the viewport scrolls down but never back up. Multi-touch gestures
 * skip that check, which is why two fingers still pan.
 *
 * The check lives in `body.ontouchmove`, reassigned every time a modal opens, so
 * it always sits after this listener. Stopping the event here skips both it and
 * the `document` level `preventDefault`, leaving the browser to scroll the modal
 * natively. Listeners inside the overlay are untouched — they run first.
 */
const useModalTouchScroll = () => {
  const handleTouchMove = useCallback((e: TouchEvent) => {
    // Only `body-scroll-lock`'s touch branch sets this, and only while locked.
    if (!document.body.ontouchmove) return;

    if (!(e.target as Element)?.closest?.(OVERLAY_SELECTOR)) return;

    e.stopImmediatePropagation();
  }, []);

  useEffect(() => {
    document.body.addEventListener('touchmove', handleTouchMove, {
      passive: true,
    });

    return () =>
      document.body.removeEventListener('touchmove', handleTouchMove);
  }, [handleTouchMove]);
};

export default useModalTouchScroll;
